import { useQuery } from '@tanstack/react-query';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuth, ROLE_ADMIN, ROLE_SALARY_ADMIN, ROLE_SALARY_VIEW, ROLE_COLLABORATOR_ADMIN, ROLE_COLLABORATOR_VIEWER, ROLE_COLLABORATOR_EDITOR } from '@/contexts/AuthContext';
import { Collaborator } from '@/models';
import { useCollaboratorSelfQuery } from './useCollaboratorSelfQuery';
import { useCollaboratorByIdQuery } from './useCollaboratorByIdQuery';
import { useCapabilityCatalogQuery } from './useCapabilityCatalogQuery';
import { resolveCollaboratorResources } from '@/features/Collaborators/utils/resourcePresentation';

export type ProfileAccessLevel = 'public' | 'self' | 'private' | 'full';

export interface CollaboratorProfileData {
    collaborator: Collaborator | null;
    accessLevel: ProfileAccessLevel;
    isLoading: boolean;
    error: Error | null;
}

export const useCollaboratorProfile = (profileId?: string): CollaboratorProfileData => {
    const { user, userHasRole } = useAuth();
    const capabilityCatalogQuery = useCapabilityCatalogQuery({ enabled: !!profileId });

    // 1. Fetch Public Data (Always)
    const { data: publicData, isLoading: isPublicLoading, error: publicError } = useQuery({
        queryKey: ['collaborator_public_detail', profileId],
        queryFn: async () => {
            if (!profileId) return null;
            const docRef = doc(db, 'collaborators_public', profileId);
            const snap = await getDoc(docRef);
            return snap.exists() ? { id: snap.id, ...snap.data() } : null;
        },
        enabled: !!profileId,
    });

    interface PublicCollaboratorData {
        id: string;
        responsibleId?: string;
        [key: string]: any;
    }

    const typedPublicData = publicData as PublicCollaboratorData | undefined;

    // 2. Determine Access Level
    let accessLevel: ProfileAccessLevel = 'public';

    if (user && profileId) {
        const isSelf = user.collaboratorId === profileId;
        const isResponsible = typedPublicData?.responsibleId === user.collaboratorId;

        const hasFullAccess = userHasRole(ROLE_ADMIN) || userHasRole(ROLE_SALARY_ADMIN) || userHasRole(ROLE_SALARY_VIEW);
        const hasPrivateAccess = userHasRole(ROLE_COLLABORATOR_ADMIN) || userHasRole(ROLE_COLLABORATOR_VIEWER) || userHasRole(ROLE_COLLABORATOR_EDITOR) || isResponsible;

        if (hasFullAccess) {
            accessLevel = 'full';
        } else if (hasPrivateAccess) {
            accessLevel = 'private';
        } else if (isSelf) {
            accessLevel = 'self';
        }
    }

    // 3. Fetch Specific Data based on Access Level

    // Private/Full Data
    const shouldFetchPrivate = accessLevel === 'private' || accessLevel === 'full';
    const { data: privateData, isLoading: isPrivateLoading, error: privateError } = useCollaboratorByIdQuery(
        shouldFetchPrivate ? profileId : undefined
    );

    // Self Data
    const shouldFetchSelf = accessLevel === 'self';
    const { data: selfData, isLoading: isSelfLoading, error: selfError } = useCollaboratorSelfQuery(
        shouldFetchSelf && profileId ? profileId : ''
    );

    // 4. Merge Data
    const isLoading =
        isPublicLoading ||
        (shouldFetchPrivate && isPrivateLoading) ||
        (shouldFetchSelf && isSelfLoading) ||
        capabilityCatalogQuery.isLoading;
    const error = publicError || (shouldFetchPrivate ? privateError : null) || (shouldFetchSelf ? selfError : null);

    const resolvedPublicData = publicData
        ? resolveCollaboratorResources(publicData as Collaborator, capabilityCatalogQuery.data)
        : null;
    const resolvedPrivateData = privateData
        ? resolveCollaboratorResources(privateData as Collaborator, capabilityCatalogQuery.data)
        : null;
    const resolvedSelfData = selfData
        ? resolveCollaboratorResources(selfData as Collaborator, capabilityCatalogQuery.data)
        : null;

    let mergedCollaborator: Collaborator | null = null;
    const baseCollaborator = resolvedPublicData ?? (shouldFetchPrivate ? resolvedPrivateData : null) ?? (shouldFetchSelf ? resolvedSelfData : null);

    if (baseCollaborator) {
        mergedCollaborator = { ...baseCollaborator } as Collaborator;

        if (shouldFetchPrivate && resolvedPrivateData) {
            mergedCollaborator = { ...mergedCollaborator, ...resolvedPrivateData };
        } else if (shouldFetchSelf && resolvedSelfData) {
            mergedCollaborator = { ...mergedCollaborator, ...resolvedSelfData };
        }

        const preferredResources =
            resolvedPrivateData?.resources ??
            resolvedSelfData?.resources ??
            resolvedPublicData?.resources;

        if (preferredResources) {
            mergedCollaborator = {
                ...mergedCollaborator,
                resources: preferredResources,
            };
        }
    }

    return {
        collaborator: mergedCollaborator,
        accessLevel,
        isLoading,
        error: error as Error | null,
    };
};
